import { useCallback, useContext, useState } from 'react';

import api, { FetchError } from '../../common/api/api';
import { apiResolve } from '../../common/api/apiResolve';
import { useRecaptchaEnterprise } from '../../hooks/recaptcha/useRecaptchaEnterprise';
import { LoginContext } from '../../login/Login.context';
import { JwtToken } from '../../user/JwtToken.interface';
import { useUserContext } from '../../user/User.context';
import { OktaLoginDto } from './Okta.interface';

function oktaLogin(dto: OktaLoginDto, recaptcha: string) {
  const request = api.post('okta/login', dto, { recaptcha });
  return { request, result: apiResolve<JwtToken>(request) };
}

function oktaSign(dto: OktaLoginDto) {
  const request = api.post('okta/sign', dto);
  return { request, result: apiResolve<{ status: string }>(request) };
}

export function useOktaLogin() {
  const [state, setState] = useState({
    isLoading: false,
    error: undefined as FetchError | undefined
  });
  const { setUser } = useUserContext();
  const { setDidLogout } = useContext(LoginContext);
  const {
    loadingRecaptchaToken,
    loadingRecaptchaTokenError,
    getRecaptchaToken
  } = useRecaptchaEnterprise();

  const login = useCallback(
    async (dto: OktaLoginDto) => {
      setState(state => ({ ...state, isLoading: true, error: undefined }));
      // get recaptcha token and login
      const recaptcha = await getRecaptchaToken('login');
      const { result } = oktaLogin(dto, recaptcha);
      // set user
      const { data, error } = await result;
      if (data) {
        // set user
        api.setAccessToken(data.accessToken);
        setUser(data.user);
        setDidLogout(false);
      }
      setState(state => ({ ...state, isLoading: false, error }));
      return { data, error };
    },
    [getRecaptchaToken, setDidLogout, setUser]
  );

  return {
    login,
    isLoading: state.isLoading || loadingRecaptchaToken,
    error: state.error || loadingRecaptchaTokenError
  };
}

export function useOktaSign() {
  const [state, setState] = useState({
    isLoading: false,
    error: undefined as FetchError | undefined
  });

  const sign = useCallback(async (dto: OktaLoginDto) => {
    setState(state => ({ ...state, isLoading: true, error: undefined }));
    const { result } = oktaSign(dto);
    // set user and session token
    const { data, error } = await result;
    setState(state => ({ ...state, isLoading: false, error }));
    return { data, error };
  }, []);

  return { ...state, sign };
}
